import { PlusIcon } from '@primer/octicons-react'
import { TrashIcon } from '@radix-ui/react-icons'
import { useQueryClient } from '@tanstack/react-query'
import { ColumnDef, RowSelectionState, createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useLocalStorage } from '@uidotdev/usehooks'
import classNames from 'classnames'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { DefaultButton } from '../components/buttons/default-button.tsx'
import { DeleteRepositoryDialog } from '../components/delete-repository-dialog.tsx'
import { UploadScanFormDrawer } from '../components/drawers/upload-scan-form-drawer.tsx'
import { WorkflowDrawer } from '../components/drawers/workflow-drawer'
import LightningIcon from '../components/icons/lightning-icon.svg?react'
import SonarWhiteIcon from '../components/icons/sonar-white-icon.svg?react'
import UploadIcon from '../components/icons/upload-icon.svg?react'
import { SearchInput } from '../components/input/search-input.tsx'
import { LatestAnalysisLink } from '../components/latest-analysis-link'
import { OnboardingDialog } from '../components/onboarding-dialog.tsx'
import { PaginationControls } from '../components/table/pagination-controls.tsx'
import { TableBodySkeleton } from '../components/table/table-body-skeleton.tsx'
import { Pagination } from '../components/table/table.tsx'
import { TooltipWithHover } from '../components/tooltip'
import { Routes } from '../routes.ts'
import {
  useDeleteRepository,
  useGetAnalyses,
  useGetAzureOrganizations,
  useGetAzureProjects,
  useGetAzureRepositories,
  useGetGitLabProjects,
  useGetIntegrations,
  useGetRepositories,
  useGetWorkflows,
  usePostAddRepository,
  usePostAnalyzeScan,
  usePostUploadScan,
} from '../utils/api-client/api-hooks.ts'
import { PaginatedRepositories, Repository } from '../utils/api-client/api-schemas.ts'
import { useAddToast } from '../utils/higher-order-components/with-toasts.tsx'
import { getEnvironmentData } from '../utils/hooks/get-environment-data.ts'
import { getRepositoryIcon } from '../utils/repository-utils'
import * as styles from './repositories-page.css.ts'
import {
  AddRepositoryModalPure,
  AzureIntegrationProps,
  GitHubIntegrationProps,
  GitlabIntegrationProps,
} from './repositories-page/add-repository-modal.tsx'

export function RepositoriesPage() {
  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [searchTerm, setSearchTerm] = useState('')

  const { handleAddToastWithTimeout } = useAddToast()

  const { data: repositories, isPending: repositoriesIsPending } = useGetRepositories({
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    searchTerm,
  })

  const environmentData = getEnvironmentData()

  const [selectedRepositoryId, setSelectedRepositoryId] = useState<string | null>(null)
  const [selectedWorkflowRepositoryId, setSelectedWorkflowRepositoryId] = useState<string | null>(null)
  const selectedRepositoryDisplayName = repositories?._embedded.items.find(repository => repository.id === selectedRepositoryId)?.name

  const handleSonarImport = useSonarImport()

  const [isOnboardingDialogOpen, setIsOnboardingDialogOpen] = useLocalStorage('isOnboardingDialogOpen', true)
  const handleCompleteOnboarding = useCallback(() => {
    setIsOnboardingDialogOpen(false)
  }, [setIsOnboardingDialogOpen])

  const [selectedRepositoryToDelete, setSelectedRepositoryToDelete] = useState<Repository | null>(null)
  const handleOpenDeleteRepositoryDialog = useCallback(
    (repositoryId: string) => {
      setSelectedRepositoryToDelete(repositories?._embedded.items.find(repository => repository.id === repositoryId) ?? null)
    },
    [repositories]
  )
  const handleCloseDeleteRepositoryDialog = useCallback(() => {
    setSelectedRepositoryToDelete(null)
  }, [setSelectedRepositoryToDelete])

  const queryClient = useQueryClient()

  const { mutate: deleteRepositoryMutation } = useDeleteRepository({
    onSuccess: async () => {
      setSelectedRepositoryToDelete(null)
      queryClient.invalidateQueries({ queryKey: ['repositories'] })
      handleAddToastWithTimeout({
        message: <>Repository deleted successfully.</>,
        variant: 'success',
      })
    },
    onError: (error: any) => {
      handleAddToastWithTimeout({
        message: <>Unable to delete repository. Please try again.</>,
        variant: 'error',
      })
      handleAddToastWithTimeout({
        message: <>{error.message}.</>,
        variant: 'error',
      })
    },
  })
  const handleDeleteRepository = useCallback(
    (repositoryId: string) => {
      deleteRepositoryMutation({ repositoryId })
    },
    [deleteRepositoryMutation]
  )

  const [addRepositoryDialogOpen, setAddRepositoryDialogOpen] = useState(false)
  const handleOpenAddRepository = useCallback(() => setAddRepositoryDialogOpen(true), [])
  const handleCloseAddRepository = useCallback(() => setAddRepositoryDialogOpen(false), [])

  const addRepositoryMutation = usePostAddRepository({
    onSuccess: () => {
      handleAddToastWithTimeout({ message: <>Repository was successfully connected.</>, variant: 'success' })
      queryClient.invalidateQueries({ queryKey: ['repositories'] })
      handleCloseAddRepository()
    },
    onError: (error: any) => {
      console.error(error)
      handleAddToastWithTimeout({
        message: <>Repository connection failed with error: {error.message}.</>,
        variant: 'error',
      })
      if (error.bodyAsText && typeof error.bodyAsText === 'string') {
        handleAddToastWithTimeout({
          message: <pre style={{ margin: '0px' }}>{error.bodyAsText}</pre>,
          variant: 'error',
        })
      }
    },
  })

  const handleConnectGitRepository: HandleConnectGitRepository = useCallback(
    ({ url, username, password, onSuccess }) => {
      const extractRepoName = (url: string): string => {
        const match = url.match(/\/([^\/]+)\.git$/)
        return match ? (match[1] ?? url) : url
      }

      addRepositoryMutation.mutate(
        {
          type: 'git',
          name: extractRepoName(url),
          credentials: {
            username,
            password,
          },
          url,
        },
        { onSuccess }
      )
    },
    [addRepositoryMutation]
  )

  const { data: paginatedIntegrations } = useGetIntegrations()
  const integrations = paginatedIntegrations?._embedded?.items ?? []
  const githubIntegration = integrations.find(integration => integration.type === 'github')
  const azureIntegration = integrations.find(integration => integration.type === 'azure')
  const gitlabIntegration = integrations.find(integration => integration.type === 'gitlab')

  // Azure form state
  const [selectedAzureOrganizationId, setSelectedAzureOrganizationId] = useState<string | undefined>()
  const [selectedAzureProjectId, setSelectedAzureProjectId] = useState<string | undefined>()
  const [selectedAzureRepositoryId, setSelectedAzureRepositoryId] = useState<string | undefined>()

  const handleChangeAzureOrganizationId = useCallback((value: string) => {
    setSelectedAzureOrganizationId(value)
    setSelectedAzureProjectId('')
    setSelectedAzureRepositoryId('')
  }, [])

  const handleChangeAzureProjectId = useCallback((value: string) => {
    setSelectedAzureProjectId(value)
    setSelectedAzureRepositoryId('')
  }, [])

  // Azure queries
  const { data: azureOrganizations } = useGetAzureOrganizations({
    enabled: !!azureIntegration,
  })
  const { data: azureProjects } = useGetAzureProjects({
    organizationName: selectedAzureOrganizationId ?? '',
    enabled: !!selectedAzureOrganizationId,
  })
  const { data: azureRepositories } = useGetAzureRepositories({
    organizationName: selectedAzureOrganizationId ?? '',
    projectId: selectedAzureProjectId ?? '',
    enabled: !!selectedAzureOrganizationId && !!selectedAzureProjectId,
  })

  const handleConnectAzureRepository: HandleConnectAzureRepository = useCallback(
    ({ selectedAzureOrganizationId, selectedAzureProjectName, selectedAzureRepositoryName }) => {
      addRepositoryMutation.mutate({
        type: 'azure',
        organization: selectedAzureOrganizationId,
        project: selectedAzureProjectName,
        name: selectedAzureRepositoryName,
      })
    },
    [addRepositoryMutation]
  )

  // Gitlab form state
  const [selectedGitLabProjectId, setSelectedGitLabProjectId] = useState<string | undefined>()
  const handleChangeGitLabProjectId = useCallback((value: string) => {
    setSelectedGitLabProjectId(value)
  }, [])
  const { data: gitlabProjects } = useGetGitLabProjects({
    search: selectedGitLabProjectId,
    enabled: !!gitlabIntegration && addRepositoryDialogOpen,
  })
  const handleConnectGitLabRepository = useCallback(
    ({ path, namespace_full_path }) => {
      addRepositoryMutation.mutate({
        type: 'gitlab',
        name: path,
        namespace: namespace_full_path,
      })
    },
    [addRepositoryMutation]
  )

  const [workflowDrawerContent, setWorkflowDrawerContent] = useState<'create' | 'manage'>('create')

  const filterRepositories = useCallback((value: string) => {
    setSearchTerm(value)
    setPagination(prev => ({ ...prev, pageIndex: 0 })) // Reset to first page on search
  }, [])

  return (
    <div className={styles.mainContainer}>
      <RepositoriesPageImpure
        repositories={repositories}
        repositoriesIsPending={repositoriesIsPending}
        pagination={pagination}
        setPagination={setPagination}
        environmentData={environmentData}
        selectedRepositoryId={selectedRepositoryId}
        setSelectedRepositoryId={setSelectedRepositoryId}
        selectedWorkflowRepositoryId={selectedWorkflowRepositoryId}
        setSelectedWorkflowRepositoryId={setSelectedWorkflowRepositoryId}
        selectedRepositoryDisplayName={selectedRepositoryDisplayName}
        handleSonarImport={handleSonarImport}
        handleCompleteOnboarding={handleCompleteOnboarding}
        isOnboardingDialogOpen={isOnboardingDialogOpen}
        selectedRepositoryToDelete={selectedRepositoryToDelete}
        handleOpenDeleteRepositoryDialog={handleOpenDeleteRepositoryDialog}
        handleCloseDeleteRepositoryDialog={handleCloseDeleteRepositoryDialog}
        handleDeleteRepository={handleDeleteRepository}
        addRepositoryDialogOpen={addRepositoryDialogOpen}
        handleOpenAddRepository={handleOpenAddRepository}
        handleCloseAddRepository={handleCloseAddRepository}
        handleConnectGitRepository={handleConnectGitRepository}
        githubIntegrationProps={
          githubIntegration
            ? {
                githubAppName: githubIntegration.app_name,
              }
            : undefined
        }
        azureIntegrationProps={
          azureIntegration
            ? {
                azureOrganizations: azureOrganizations?._embedded?.items ?? [],
                azureProjects: azureProjects?._embedded?.items ?? [],
                azureRepositories: azureRepositories?._embedded?.items ?? [],
                selectedAzureOrganizationId,
                selectedAzureProjectId,
                selectedAzureRepositoryId,
                handleChangeAzureOrganizationId,
                handleChangeAzureProjectId,
                handleChangeAzureRepositoryId: setSelectedAzureRepositoryId,
                handleConnectAzureRepository,
              }
            : undefined
        }
        gitlabIntegrationProps={
          gitlabIntegration
            ? {
                gitlabProjects: gitlabProjects?._embedded?.items ?? [],
                selectedGitLabProjectId,
                handleChangeGitLabProjectId,
                handleConnectGitLabRepository,
              }
            : undefined
        }
        workflowDrawerContent={workflowDrawerContent}
        setWorkflowDrawerContent={setWorkflowDrawerContent}
        searchTerm={searchTerm}
        handleChangeSearchTerm={filterRepositories}
      />
    </div>
  )
}

function RepositoriesPageImpure({
  repositories,
  repositoriesIsPending,
  pagination,
  setPagination,
  environmentData,
  selectedRepositoryId,
  setSelectedRepositoryId,
  selectedWorkflowRepositoryId,
  setSelectedWorkflowRepositoryId,
  selectedRepositoryDisplayName,
  handleSonarImport,
  handleCompleteOnboarding,
  isOnboardingDialogOpen,
  selectedRepositoryToDelete,
  handleOpenDeleteRepositoryDialog,
  handleCloseDeleteRepositoryDialog,
  handleDeleteRepository,
  addRepositoryDialogOpen,
  handleOpenAddRepository,
  handleCloseAddRepository,
  handleConnectGitRepository,
  githubIntegrationProps,
  azureIntegrationProps,
  gitlabIntegrationProps,
  workflowDrawerContent,
  setWorkflowDrawerContent,
  searchTerm,
  handleChangeSearchTerm,
}: {
  repositories: PaginatedRepositories
  repositoriesIsPending: boolean
  pagination: Pagination
  setPagination: React.Dispatch<React.SetStateAction<Pagination>>
  environmentData: ReturnType<typeof getEnvironmentData>
  selectedRepositoryId: string | null
  setSelectedRepositoryId: React.Dispatch<React.SetStateAction<string | null>>
  selectedWorkflowRepositoryId: string | null
  setSelectedWorkflowRepositoryId: React.Dispatch<React.SetStateAction<string | null>>
  selectedRepositoryDisplayName: string | undefined
  handleSonarImport: (repositoryId: string) => void
  handleCompleteOnboarding: () => void
  isOnboardingDialogOpen: boolean
  selectedRepositoryToDelete: Repository | null
  handleOpenDeleteRepositoryDialog: (repositoryId: string) => void
  handleCloseDeleteRepositoryDialog: () => void
  handleDeleteRepository: (repositoryId: string) => void
  addRepositoryDialogOpen: boolean
  handleOpenAddRepository: () => void
  handleCloseAddRepository: () => void
  handleConnectGitRepository: HandleConnectGitRepository
  githubIntegrationProps?: GitHubIntegrationProps
  azureIntegrationProps?: AzureIntegrationProps
  gitlabIntegrationProps?: GitlabIntegrationProps
  workflowDrawerContent: 'create' | 'manage'
  setWorkflowDrawerContent: React.Dispatch<React.SetStateAction<'create' | 'manage'>>
  searchTerm: string
  handleChangeSearchTerm: (value: string) => void
}) {
  return (
    <div className={styles.topLevelContainer}>
      {isOnboardingDialogOpen && <OnboardingDialog handleCompleteOnboarding={handleCompleteOnboarding} />}
      {selectedRepositoryId && selectedRepositoryDisplayName && (
        <UploadScanFormDrawer
          repositoryId={selectedRepositoryId}
          repositoryDisplayName={selectedRepositoryDisplayName}
          handleCloseDrawer={() => setSelectedRepositoryId(null)}
        />
      )}
      {selectedRepositoryToDelete && (
        <DeleteRepositoryDialog
          repository={selectedRepositoryToDelete}
          handleDeleteRepository={handleDeleteRepository}
          handleCloseDeleteRepositoryDialog={handleCloseDeleteRepositoryDialog}
        />
      )}
      {selectedWorkflowRepositoryId && (
        <WorkflowDrawer
          repositoryId={selectedWorkflowRepositoryId}
          repositoryDisplayName={repositories?._embedded.items.find(repo => repo.id === selectedWorkflowRepositoryId)?.name ?? ''}
          repositoryType={repositories?._embedded.items.find(repo => repo.id === selectedWorkflowRepositoryId)?.type ?? 'git'}
          handleCloseDrawer={() => {
            setSelectedWorkflowRepositoryId(null)
            setWorkflowDrawerContent('create')
          }}
          drawerContent={workflowDrawerContent}
          onSetDrawerContent={setWorkflowDrawerContent}
        />
      )}
      {addRepositoryDialogOpen && (
        <AddRepositoryModalPure
          environmentName={environmentData.name}
          onClose={handleCloseAddRepository}
          handleConnectGitRepository={handleConnectGitRepository}
          githubIntegrationProps={githubIntegrationProps}
          azureIntegrationProps={azureIntegrationProps}
          gitlabIntegrationProps={gitlabIntegrationProps}
        />
      )}
      <RepositoriesTable
        repositories={repositories}
        repositoriesIsPending={repositoriesIsPending}
        pagination={pagination}
        setPagination={setPagination}
        environmentData={environmentData}
        setSelectedRepositoryId={setSelectedRepositoryId}
        setSelectedWorkflowRepositoryId={setSelectedWorkflowRepositoryId}
        handleSonarImport={handleSonarImport}
        handleOpenDeleteRepositoryDialog={handleOpenDeleteRepositoryDialog}
        handleOpenAddRepository={handleOpenAddRepository}
        setWorkflowDrawerContent={setWorkflowDrawerContent}
        searchTerm={searchTerm}
        handleChangeSearchTerm={handleChangeSearchTerm}
      />
    </div>
  )
}

function RepositoriesTable({
  repositories,
  repositoriesIsPending,
  pagination,
  setPagination,
  environmentData,
  setSelectedRepositoryId,
  setSelectedWorkflowRepositoryId,
  handleSonarImport,
  handleOpenDeleteRepositoryDialog,
  handleOpenAddRepository,
  setWorkflowDrawerContent,
  searchTerm,
  handleChangeSearchTerm,
}: {
  repositories: PaginatedRepositories
  repositoriesIsPending: boolean
  pagination: Pagination
  setPagination: React.Dispatch<React.SetStateAction<Pagination>>
  environmentData: ReturnType<typeof getEnvironmentData>
  setSelectedRepositoryId: React.Dispatch<React.SetStateAction<string | null>>
  setSelectedWorkflowRepositoryId: React.Dispatch<React.SetStateAction<string | null>>
  handleSonarImport: (repositoryId: string) => void
  handleOpenDeleteRepositoryDialog: (repositoryId: string) => void
  handleOpenAddRepository: () => void
  setWorkflowDrawerContent: React.Dispatch<React.SetStateAction<'create' | 'manage'>>
  searchTerm: string
  handleChangeSearchTerm: (value: string) => void
}) {
  const columns = useMemo(
    () =>
      createColumnsDefinitions(
        setSelectedRepositoryId,
        setSelectedWorkflowRepositoryId,
        handleSonarImport,
        handleOpenDeleteRepositoryDialog,
        setWorkflowDrawerContent
      ),
    [setSelectedRepositoryId, setSelectedWorkflowRepositoryId, handleSonarImport, handleOpenDeleteRepositoryDialog, setWorkflowDrawerContent]
  )
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  const table = useReactTable({
    getRowId: data => String(data.id),
    data: repositories._embedded.items,
    columns,
    enableRowSelection: true,
    enableGlobalFilter: false,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
    },
  })

  return (
    <>
      <div className={styles.tableControls}>
        <div>
          <h4 className={styles.tableHeading}>
            Repositories <span className={styles.installationsCount}>({repositories.total})</span>
          </h4>
          <p className={styles.tableDescription}>Locations where {environmentData.githubAppName} has been configured for analysis</p>
        </div>

        <div className={styles.tableControlsRight}>
          <SearchInput
            value={searchTerm}
            onChange={value => handleChangeSearchTerm(String(value))}
            size="small"
            debounce={300}
            name="Search repositories"
          />
          <DefaultButton size="small" onClick={handleOpenAddRepository}>
            + &nbsp; Connect new
          </DefaultButton>
        </div>
      </div>

      <table className={styles.table}>
        <thead className={styles.tableHead}>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                return (
                  <th key={header.id} className={classNames(styles.columnHeader, styles.cell, styles.installationsTableCell)}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>
        {repositoriesIsPending ? (
          <TableBodySkeleton rowCount={7} columnCount={columns.length} rowHeightInPixels={43} />
        ) : (
          <tbody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map(row => (
                <tr key={row.id} className={styles.row}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className={classNames(styles.cell, styles.installationsTableCell)}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td className={classNames(styles.cell, styles.emptyStateCell)} colSpan={columns.length}>
                  <div className={styles.emptyStateText}>
                    {searchTerm ? 'No repositories match your search' : 'No repositories found. Please connect your first repository.'}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        )}
      </table>

      <div className={styles.tableFooter}>
        <PaginationControls
          tableLabel="Installations"
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          rowCount={repositories.total}
          setPageIndex={pageIndex => setPagination({ pageIndex, pageSize: pagination.pageSize })}
          setPageSize={pageSize => setPagination({ pageIndex: pagination.pageIndex, pageSize })}
        />
      </div>
    </>
  )
}

function RepoLocation({ repository }: { repository: Repository }) {
  return (
    <span className={styles.repositoryName}>
      <div style={{ marginRight: '12px' }}>{getRepositoryIcon({ type: repository.type, className: styles.scmIcon })}</div>
      {repository.name}
    </span>
  )
}

const useSonarImport = () => {
  const navigate = useNavigate()
  const { handleAddToastWithTimeout } = useAddToast()

  const analyzeScanMutation = usePostAnalyzeScan({
    onError: (error: any) => {
      handleAddToastWithTimeout({
        message: <>Analysis trigger failed. You can try to analyze it again later. {error.message}.</>,
        variant: 'error',
      })
    },
  })

  const uploadScanMutation = usePostUploadScan({
    onSuccess: ({ id: scanId }) => {
      handleAddToastWithTimeout({
        message: <>Sonar import started successfully.</>,
        variant: 'success',
      })

      analyzeScanMutation.mutate(
        { scanId },
        {
          onSuccess: ({ id: analysisId }) => navigate(Routes.AnalysisDetailsPage.createPath({ analysisId })),
        }
      )
    },
    onError: (error: any) => {
      handleAddToastWithTimeout({
        message: <>{`Unable to import Sonar findings: ${error.message}. Please try again.`}</>,
        variant: 'error',
      })
    },
  })

  return useCallback(
    (repositoryId: string) => {
      const data = {
        repositoryId,
        metadata: {
          tool: 'sonar' as const,
          sha: '',
        },
        files: {},
      }

      uploadScanMutation.mutate({ repositoryId, body: data })
    },
    [uploadScanMutation]
  )
}

const createColumnsDefinitions = (
  setSelectedRepositoryId: React.Dispatch<React.SetStateAction<string | null>>,
  setSelectedWorkflowRepositoryId: React.Dispatch<React.SetStateAction<string | null>>,
  handleSonarImport: (repositoryId: string) => void,
  handleOpenDeleteRepositoryDialog: (repositoryId: string) => void,
  setWorkflowDrawerContent: React.Dispatch<React.SetStateAction<'create' | 'manage'>>
): ColumnDef<Repository>[] => {
  const columnHelper = createColumnHelper<Repository>()

  return [
    columnHelper.accessor((row: Repository): string => row.name, {
      id: 'repository',
      cell: ({ row }) => {
        const { data: workflows } = useGetWorkflows({ repositoryId: row.original.id })
        const hasWorkflows = workflows._embedded.items.length > 0

        return (
          <div className={styles.repositoryNameContainer}>
            <RepoLocation repository={row.original} />
            {hasWorkflows && (
              <TooltipWithHover
                trigger={
                  <button
                    className={styles.workflowButton}
                    onClick={() => {
                      setWorkflowDrawerContent('manage')
                      setSelectedWorkflowRepositoryId(row.original.id)
                    }}
                    aria-label="Manage workflows"
                  >
                    <LightningIcon className={styles.workflowIndicator} />
                  </button>
                }
                size="small"
              >
                <span>Manage workflows</span>
              </TooltipWithHover>
            )}
          </div>
        )
      },
      header: () => <span>REPOSITORY</span>,
    }) as ColumnDef<Repository>,
    columnHelper.display({
      id: 'fixes',
      cell: () => <></>,
      header: () => <></>,
    }),
    columnHelper.display({
      id: 'analysis',
      cell: ({ row }) => {
        const { data: analyses } = useGetAnalyses({
          repositoryId: row.original.id,
          pageSize: 1,
          pageIndex: 0,
        })
        const latestAnalysis = analyses?._embedded?.items?.[0]

        return (
          <div className={styles.center}>
            {latestAnalysis ? (
              <LatestAnalysisLink latestAnalysis={latestAnalysis} />
            ) : (
              <a
                href="#"
                className={styles.uploadScanLink}
                onClick={e => {
                  e.preventDefault()
                  setSelectedRepositoryId(row.original.id)
                }}
              >
                Upload scan
                <UploadIcon className={styles.uploadIconThemed} />
              </a>
            )}
          </div>
        )
      },
      header: ({}) => <span className={styles.center}>LATEST ANALYSIS</span>,
    }),
    columnHelper.display({
      id: 'actions',
      cell: ({ row }) => {
        const { data: analyses } = useGetAnalyses({
          repositoryId: row.original.id,
          pageSize: 1,
          pageIndex: 0,
        })
        const latestAnalysis = analyses?._embedded?.items?.[0]

        return (
          <div className={styles.center}>
            {latestAnalysis && (
              <TooltipWithHover
                trigger={
                  <button className={styles.actionButton} onClick={() => setSelectedRepositoryId(row.original.id)}>
                    <UploadIcon className={styles.uploadIconThemed} />
                  </button>
                }
                size="small"
              >
                <span>Upload scan</span>
              </TooltipWithHover>
            )}
            <TooltipWithHover
              trigger={
                <button className={styles.actionButton} onClick={() => setSelectedWorkflowRepositoryId(row.original.id)} aria-label="New workflow">
                  <PlusIcon aria-hidden="true" />
                </button>
              }
              size="small"
            >
              <span>New workflow</span>
            </TooltipWithHover>
            <TooltipWithHover
              trigger={
                <button className={styles.actionButton} onClick={() => handleSonarImport(row.original.id)} aria-label="Import Sonar findings">
                  <SonarWhiteIcon />
                </button>
              }
              size="small"
            >
              <span>Import Sonar findings</span>
            </TooltipWithHover>
            <TooltipWithHover
              trigger={
                <button
                  className={styles.actionButton}
                  onClick={() => handleOpenDeleteRepositoryDialog(row.original.id)}
                  aria-label="Open delete repository dialog"
                >
                  <TrashIcon />
                </button>
              }
              size="small"
            >
              <span>Delete repository</span>
            </TooltipWithHover>
          </div>
        )
      },
      header: () => 'Actions',
    }),
  ]
}

export type HandleConnectGitRepository = (params: { url: string; username: string; password: string; onSuccess: () => void }) => void

export type HandleConnectAzureRepository = (params: {
  selectedAzureOrganizationId: string
  selectedAzureProjectName: string
  selectedAzureRepositoryName: string
}) => void
